/**
 * use-global-search — hook globalnej wyszukiwarki tenanta (paleta Ctrl+K + pole w pasku).
 *
 * Woła `GET /api/search?q=` (tenant scope) i zwraca wyniki pogrupowane po źródle. Grupy
 * z bazy (kontrahenci/pracownicy/użytkownicy/dokumenty) liczy backend; grupy nawigacyjne
 * (Pozycje menu / Ustawienia) składa `buildNavigationHits` po stronie frontu ze statycznych
 * tras SPA — patrz GlobalSearchModal.
 */

import type { DocumentClass } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

// Re-eksport zostaje, bo `GlobalSearch.tsx` importuje ten typ stąd; źródłem prawdy jest `@saas/shared`.
export type { DocumentClass };

export interface SearchHit {
  id: string;
  title: string;
  subtitle: string | null;
}

export interface DocumentSearchHit extends SearchHit {
  documentClass: DocumentClass;
}

export interface GlobalSearchResults {
  contractors: SearchHit[];
  employees: SearchHit[];
  users: SearchHit[];
  documents: DocumentSearchHit[];
}

export interface GlobalSearchResponse {
  q: string;
  results: GlobalSearchResults;
  total: number;
}

/** Minimalna długość frazy — spójna z backendem (poniżej nie odpytujemy). */
export const MIN_SEARCH_LEN = 2;

export function useGlobalSearch(query: string, opts?: { enabled?: boolean }) {
  const term = query.trim();
  const enabled = (opts?.enabled ?? true) && term.length >= MIN_SEARCH_LEN;
  return useQuery<GlobalSearchResponse>({
    queryKey: ['tenant', 'global-search', term],
    queryFn: async () => {
      const res = await api.get<GlobalSearchResponse>('/search', { params: { q: term } });
      return res.data;
    },
    enabled,
    staleTime: 15_000,
  });
}
